/**
 * Build-time environment config.
 *
 * Vite inlines import.meta.env at build time. Only variables prefixed with
 * VITE_ are exposed to the client. Set them in .env, .env.local, .env.[mode],
 * or the host environment when running the build.
 *
 * @see https://vitejs.dev/guide/env-and-mode
 */

function getEnv(key: string): string | undefined {
  if (typeof import.meta === "undefined" || import.meta.env == null) {
    return undefined;
  }
  const v = import.meta.env[key];
  return typeof v === "string" ? v : undefined;
}

function getEnvInt(key: string, fallback: number): number {
  const raw = getEnv(key)?.trim();
  if (!raw) return fallback;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

/** API base URL for the axios instance. Set explicitly via VITE_API_BASE_URL. */
export const env = {
  /** Base URL for API requests (e.g. https://api.example.com). */
  apiBaseUrl: getEnv("VITE_API_BASE_URL") ?? "",
  /** Vite mode: "development" | "production" | "test" */
  mode: getEnv("MODE") ?? "development",
  /** true when running in dev (npm run dev) */
  dev: import.meta.env?.DEV === true,
  /** true when building for production */
  prod: import.meta.env?.PROD === true,
  /**
   * Whether the internal "AI Insights (test)" harness page/route/settings
   * card should be available in this build. Defaults to enabled for local
   * dev (npm run dev) so it isn't accidentally hidden without explicit
   * config, but must be explicitly set to "true" via VITE_ENABLE_AI_INSIGHTS_TEST_PAGE
   * for any deployed (stg/prod) environment build.
   */
  enableAiInsightsTestPage: getEnv("VITE_ENABLE_AI_INSIGHTS_TEST_PAGE") === "true" || import.meta.env?.DEV === true,
  /** Developer insight tooling is compiled into local development builds only. */
  enableAiInsightsDevPage: import.meta.env?.DEV === true,
  /**
   * Whether the dev date picker and debug panel are visible.
   * Always on in local dev (npm run dev), or when VITE_ENABLE_DEV_TOOLS=true
   * is set in the deployed environment (e.g. a staging/dev server).
   */
  enableDevTools: getEnv("VITE_ENABLE_DEV_TOOLS") === "true" || import.meta.env?.DEV === true,
  /**
   * Whether the global AI chatbot overlay menu is available in this build.
   * Defaults to enabled unless explicitly set to "false".
   */
  enableChatbotMenu: (getEnv("VITE_ENABLE_CHATBOT_MENU") ?? "").toLowerCase() !== "false",
  /**
   * Whether RAID insight UI should show provenance markers beside content
   * generated by the AI. This is intentionally opt-in, even in local dev.
   */
  showAiInsightsSources: getEnv("VITE_DEV_SHOW_INSIGHTS_SOURCES") === "true",
  /**
   * Shared polling interval (ms) for UI background refresh loops.
   * Defaults to 10 seconds when not explicitly configured.
   */
  uiPollIntervalMs: getEnvInt("VITE_UI_POLL_INTERVAL_MS", 10_000),
} as const;

/** Resolved API base URL: env.apiBaseUrl only. */
export const apiBaseUrl = env.apiBaseUrl.trim().replace(/\/$/, "");
